/* eslint-disable react-hooks/exhaustive-deps */
/**
 * Phase A R6 — Auto-DJ panel (ref-based state machine).
 *
 * Why the R6 rewrite?
 *   Round 5 used React state for `playlist`, `activeDeck`, `blending` AND
 *   listed those in the tick `useEffect` dep array. Each crossfade fired
 *   `setPlaylist(p => p.slice(1))` which re-mounted the tick effect MID-
 *   crossfade, spawning overlapping blends and losing the deck pointer
 *   after the second transition. Result: Auto-DJ stopped after track 2.
 *
 *   R6 fixes this by holding all run-state in refs (so the tick effect
 *   only re-mounts on `enabled` toggle) and using a `currentIndex` cursor
 *   instead of popping the head of the playlist. Played tracks remain in
 *   the visible list (highlighted via cursor) so the DJ can see history
 *   and the pop-out close/reopen flow keeps everything consistent.
 *
 *   The Mix Now button moved into this panel per Round 6 B1 spec.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Play, Pause, Plus, X, GripVertical, Music, AlertCircle, RefreshCw, Lightbulb } from "lucide-react";

const CAMELOT_NEIGHBOURS = (k) => {
  if (!k) return new Set();
  const num = parseInt(k, 10);
  const letter = k.slice(-1);
  const wrap = (n) => ((n - 1 + 12) % 12) + 1;
  return new Set([
    `${num}${letter}`,
    `${wrap(num - 1)}${letter}`,
    `${wrap(num + 1)}${letter}`,
    `${num}${letter === "A" ? "B" : "A"}`,
  ]);
};

function fmtMs(ms) {
  if (!isFinite(ms) || ms <= 0) return "0:00";
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

export default function AutoDjPanel({
  engineRef,
  playlist,
  setPlaylist,
  onLoadNext,         // (track, targetDeck) → Promise<{duration, bpm, key}>
  onReattach,         // (track) → void; resolve library file + attach to engine
  onMixNow,           // () => string|undefined; engine-level mix now (used when Auto-DJ is OFF)
  initialCrossfade = 8,
  initialKeyMatch = true,
  initialBpmMatch = true,
  onLogEvent = () => {},
}) {
  // -- React state (drives renders) --
  const [enabled, setEnabled] = useState(false);
  const [crossfade, setCrossfade] = useState(() => {
    try { return parseInt(localStorage.getItem("sr-autodj-xf") || "", 10) || initialCrossfade; }
    catch { return initialCrossfade; }
  });
  const [keyMatch, setKeyMatch] = useState(initialKeyMatch);
  const [bpmMatch, setBpmMatch] = useState(initialBpmMatch);
  const [maxLengthSec, setMaxLengthSec] = useState(() => {
    try { return parseInt(localStorage.getItem("sr-autodj-maxlen") || "", 10) || 0; }
    catch { return 0; }
  });
  // Phase A R9 G — transition style selector. 5 styles; persisted to
  // localStorage. Used by both Auto-DJ tick crossfades and manual
  // Mix Now. The engine implements each style via `engine.transition`.
  const [transitionStyle, setTransitionStyle] = useState(() => {
    try { return localStorage.getItem("sr-autodj-style") || "linear"; }
    catch { return "linear"; }
  });
  const [currentIndex, setCurrentIndex] = useState(-1);  // cursor; -1 = nothing started yet
  const [activeDeck, setActiveDeck]     = useState("A");
  const [countdown, setCountdown]       = useState(null);
  const [blending, setBlending]         = useState(false);
  const [warning, setWarning]           = useState("");

  useEffect(() => { try { localStorage.setItem("sr-autodj-xf",     String(crossfade)); } catch (err) { /* eslint-disable-next-line no-console */ console.warn("[autodj] sr-autodj-xf persist failed:", err); } }, [crossfade]);
  useEffect(() => { try { localStorage.setItem("sr-autodj-maxlen", String(maxLengthSec)); } catch (err) { /* eslint-disable-next-line no-console */ console.warn("[autodj] sr-autodj-maxlen persist failed:", err); } }, [maxLengthSec]);
  useEffect(() => { try { localStorage.setItem("sr-autodj-style",  transitionStyle); } catch (err) { /* eslint-disable-next-line no-console */ console.warn("[autodj] sr-autodj-style persist failed:", err); } }, [transitionStyle]);

  // -- Refs (read inside the long-lived tick; never trigger re-mounts) --
  const playlistRef     = useRef(playlist);
  const currentIndexRef = useRef(-1);
  const activeDeckRef   = useRef("A");
  const blendingRef     = useRef(false);
  const enabledRef      = useRef(false);
  const crossfadeRef    = useRef(crossfade);
  const keyMatchRef     = useRef(keyMatch);
  const bpmMatchRef     = useRef(bpmMatch);
  const maxLengthSecRef = useRef(maxLengthSec);
  const transitionStyleRef = useRef(transitionStyle);
  // (R7 cleanup) — the R5 `mixNowSignalRef` was removed; handleMixNow now
  // performs the load+crossfade directly instead of poking a tick signal.

  useEffect(() => { playlistRef.current     = playlist;     }, [playlist]);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);
  useEffect(() => { activeDeckRef.current   = activeDeck;   }, [activeDeck]);
  useEffect(() => { enabledRef.current      = enabled;      }, [enabled]);
  useEffect(() => { crossfadeRef.current    = crossfade;    }, [crossfade]);
  useEffect(() => { keyMatchRef.current     = keyMatch;     }, [keyMatch]);
  useEffect(() => { bpmMatchRef.current     = bpmMatch;     }, [bpmMatch]);
  useEffect(() => { maxLengthSecRef.current = maxLengthSec; }, [maxLengthSec]);
  useEffect(() => { transitionStyleRef.current = transitionStyle; }, [transitionStyle]);

  // Total set length (sum of all queued tracks)
  const totalMs = useMemo(
    () => playlist.reduce((s, t) => s + ((t.durationSec ?? t.duration) || 0) * 1000, 0),
    [playlist]
  );

  // Total time remaining (active deck remainder + queued tail)
  const [remainingMs, setRemainingMs] = useState(totalMs);
  useEffect(() => {
    const id = setInterval(() => {
      const eng = engineRef.current;
      const deck = eng ? (activeDeckRef.current === "A" ? eng.deckA : eng.deckB) : null;
      const activeRem = deck ? Math.max(0, (deck.duration || 0) - (deck.currentTime || 0)) : 0;
      const ci = currentIndexRef.current;
      const tail = playlistRef.current
        .slice(ci + 1)
        .reduce((s, t) => s + ((t.durationSec ?? t.duration) || 0), 0);
      setRemainingMs(Math.round((activeRem + tail) * 1000));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // Pick the next playable track after `afterIndex`. Tracks without a File
  // (post-refresh, pre-Re-attach) are skipped. Key-match preference is a
  // best-effort 5-track lookahead; falls back to "any playable" if no key
  // neighbour exists.
  const pickNext = (afterIndex) => {
    const list = playlistRef.current;
    const eng  = engineRef.current;
    const currentDeck = eng?.[activeDeckRef.current === "A" ? "deckA" : "deckB"];
    const currentKey  = currentDeck?.detectedKey || currentDeck?.key;
    const accept = (keyMatchRef.current && currentKey) ? CAMELOT_NEIGHBOURS(currentKey) : null;

    for (let i = afterIndex + 1; i < Math.min(list.length, afterIndex + 6); i++) {
      const t = list[i];
      if (!t.file && !t.hasFile) continue;
      if (accept) {
        const k = t.detectedKey || t.key;
        if (k && !accept.has(k)) continue;
      }
      return { index: i, track: t };
    }
    for (let i = afterIndex + 1; i < list.length; i++) {
      const t = list[i];
      if (!t.file && !t.hasFile) continue;
      return { index: i, track: t };
    }
    return null;
  };

  const runCrossfade = async (fromDeck, toDeck, durSec) => {
    const eng = engineRef.current; if (!eng) return;
    const style = transitionStyleRef.current || "linear";
    // eslint-disable-next-line no-console
    console.log(`[transition] style=${style} from=${fromDeck} to=${toDeck} dur=${durSec}s`);

    // Phase A R11 A/B — echo + loop need engine-level orchestration
    // (FX wet bus, true loop locking). Delegate to engine.transition()
    // which runs in the main tab. RemoteEngine.transition proxies via
    // BC and awaits the ack. Linear / eqswap / filter / (legacy echo
    // placeholder removed) continue with the per-step loop below.
    if (style === "echo" || style === "loop") {
      try {
        await eng.transition(fromDeck, toDeck, style, durSec);
        return;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(`[transition] engine.transition(${style}) failed; falling back to linear:`, err);
        try { window.dispatchEvent(new CustomEvent("sr-toast", { detail: { type: "warning", msg: `${style} transition fallback to linear: ${err?.message || err}` } })); } catch { /* */ }
        // Fall through to linear path below.
      }
    }

    const fromDeckRef = fromDeck === "A" ? eng.deckA : eng.deckB;
    const toDeckRef   = toDeck   === "A" ? eng.deckA : eng.deckB;
    const xfDir  = toDeck === "A" ? -1 : 1;
    const steps  = 30;

    // Reset modifiers on the INCOMING deck so it starts at neutral EQ/filter.
    try { toDeckRef?.setLow?.(0); } catch { /* */ }
    try { toDeckRef?.setFilter?.(0); } catch { /* */ }

    for (let i = 1; i <= steps; i++) {
      if (!enabledRef.current) break;
      const t = i / steps;                 // 0..1 progress
      const v = xfDir * t;                  // crossfader target
      try { eng.setCrossfader(v); } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[autodj] runCrossfade setCrossfader threw:", err);
      }
      // Phase A R9 G — apply transition-style modifiers per step.
      if (style === "eqswap") {
        try { fromDeckRef?.setLow?.(-t); } catch { /* */ }
        try { toDeckRef?.setLow?.(0); } catch { /* */ }
      } else if (style === "filter") {
        try { fromDeckRef?.setFilter?.(t); } catch { /* */ }
      }
      // 'linear' = default: just crossfader, no extra modifiers.
      await new Promise((r) => setTimeout(r, (durSec * 1000) / steps));
    }
    // Reset EQ / filter on outgoing so it's neutral when next reloaded.
    try { fromDeckRef?.setLow?.(0); } catch { /* */ }
    try { fromDeckRef?.setFilter?.(0); } catch { /* */ }
    try { fromDeckRef.pause(); } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`[autodj] runCrossfade pause(${fromDeck}) threw:`, err);
    }
  };

  // Single long-lived tick. Deps are `[enabled]` ONLY — the effect does
  // NOT re-mount when playlist / currentIndex / activeDeck change. That's
  // the bug the R5 implementation had.
  useEffect(() => {
    if (!enabled) { setCountdown(null); setWarning(""); return; }
    let id = 0;
    let stopped = false;

    const tick = async () => {
      if (stopped || !enabledRef.current) return;
      const eng = engineRef.current;
      if (!eng) { id = setTimeout(tick, 500); return; }

      // Autostart on enable: load the first playable track onto A and play.
      const anyPlaying = !!eng.deckA?.playing || !!eng.deckB?.playing;
      if (!anyPlaying && !blendingRef.current && currentIndexRef.current < 0) {
        const list = playlistRef.current;
        // eslint-disable-next-line no-console
        console.log(`[autodj] start clicked, playlist.length=${list.length} currentIndex=${currentIndexRef.current} activeDeck=${activeDeckRef.current}`);
        const startIdx = list.findIndex((t) => !!t.file || !!t.hasFile);
        if (startIdx < 0) {
          // eslint-disable-next-line no-console
          console.warn(`[autodj] no playable track found in playlist (all rows missing file + hasFile)`);
          id = setTimeout(tick, 600); return;
        }
        const first = list[startIdx];
        // eslint-disable-next-line no-console
        console.log(`[autodj] track at currentIndex=${startIdx}:`, { trackId: first.trackId, name: first.name, hasFile: !!first.file, hasFileFlag: !!first.hasFile, fileType: first.file?.type, fileSize: first.file?.size });
        blendingRef.current = true;
        try {
          // eslint-disable-next-line no-console
          console.log(`[autodj] calling engine.loadFile (via onLoadNext) deck=A file=${first.name}`);
          await onLoadNext?.(first, "A");
          // eslint-disable-next-line no-console
          console.log(`[autodj] onLoadNext returned. ctx.state=${eng.ctx?.state} deckA.duration=${eng.deckA?.duration} deckA.audioEl=${!!eng.deckA?.audioEl}`);
          try { eng.setCrossfader(-1); } catch (err) {
            // eslint-disable-next-line no-console
            console.error("[autodj] setCrossfader threw:", err);
          }
          // eslint-disable-next-line no-console
          console.log(`[autodj] calling deckA.play()`);
          try { await eng.deckA?.play(); } catch (err) {
            // eslint-disable-next-line no-console
            console.error("[autodj] deckA.play() threw:", err);
            try { window.dispatchEvent(new CustomEvent("sr-toast", { detail: { type: "error", msg: `Auto-DJ play failed: ${err?.message || err}. Tap the page first.` } })); } catch { /* */ }
          }
          // eslint-disable-next-line no-console
          console.log(`[autodj] play() invoked, deckA.playing=${eng.deckA?.playing}`);
          currentIndexRef.current = startIdx; setCurrentIndex(startIdx);
          activeDeckRef.current = "A"; setActiveDeck("A");
          onLogEvent?.(`Auto-DJ: started with ${first.name}`);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(`[autodj] autostart FAILED for ${first?.name}:`, e);
          onLogEvent?.(`Auto-DJ: failed to start (${e?.message || e})`);
          try { window.dispatchEvent(new CustomEvent("sr-toast", { detail: { type: "error", msg: `Auto-DJ start failed: ${e?.message || e}` } })); } catch { /* */ }
        }
        blendingRef.current = false;
        id = setTimeout(tick, 800);
        return;
      }

      // Schedule the next transition. Reads everything from refs.
      const D       = activeDeckRef.current;
      const otherD  = D === "A" ? "B" : "A";
      const deck    = D       === "A" ? eng.deckA : eng.deckB;
      const other   = otherD  === "A" ? eng.deckA : eng.deckB;

      // If the active deck isn't actually playing yet (still loading),
      // wait a tick. Avoids spurious "0 < 0" countdowns.
      if (!deck?.duration || !deck.playing) {
        // Phase A R9 B — surface why tick is parking. Common silent
        // failure: deck.playing flipped to false because audio reached
        // natural EOF (short test tracks or main-tab pause race).
        // eslint-disable-next-line no-console
        if (Math.random() < 0.05) console.log(`[autodj] tick parked: D=${D} duration=${deck?.duration} playing=${deck?.playing} currentIndex=${currentIndexRef.current}`);
        id = setTimeout(tick, 500); return;
      }

      const trackEnd = deck.duration;
      const mlen     = maxLengthSecRef.current;
      const limit    = mlen > 0 ? Math.min(mlen, trackEnd) : trackEnd;
      const xfTime   = Math.max(2, crossfadeRef.current || 8);
      const startXf  = Math.max(0, limit - xfTime);
      const tToStart = startXf - (deck.currentTime || 0);
      setCountdown(tToStart > 0 ? Math.ceil(tToStart) : 0);

      const naturalTrigger = tToStart <= 0;
      const trigger = naturalTrigger && !blendingRef.current;

      if (trigger) {
        const pick = pickNext(currentIndexRef.current);
        // eslint-disable-next-line no-console
        console.log(`[autodj] TRANSITION fires: from D=${D} (deck=${deck?.title || '?'} t=${deck.currentTime?.toFixed(2)}/${trackEnd?.toFixed(2)}) currentIndex=${currentIndexRef.current} → pick=${pick ? `index=${pick.index} name='${pick.track.name}' trackId=${pick.track.trackId} hasFile=${!!pick.track.file} hasFileFlag=${!!pick.track.hasFile}` : 'NULL (end of queue)'}`);
        if (!pick) {
          // End of queue. Let the current track play out.
          setWarning("Auto-DJ: queue ended, playing through.");
          setCountdown(null);
          id = setTimeout(tick, 2000);
          return;
        }

        blendingRef.current = true;
        setBlending(true);
        setWarning("");

        try {
          // eslint-disable-next-line no-console
          console.log(`[autodj] loading next onto ${otherD} via onLoadNext: ${pick.track.name}`);
          // Load the next track onto the OTHER deck.
          const meta = await onLoadNext?.(pick.track, otherD);
          // eslint-disable-next-line no-console
          console.log(`[autodj] onLoadNext for ${otherD} resolved: duration=${meta?.duration} bpm=${meta?.bpm}; other.duration=${other?.duration} other.audioEl=${!!other?.audioEl}`);
          // Optional BPM match — tighten the incoming pitch toward the
          // currently-playing BPM (clamped to ±8%).
          if (bpmMatchRef.current && deck.effectiveBpm && meta?.bpm) {
            const ratio = deck.effectiveBpm / meta.bpm;
            const pct = Math.max(-8, Math.min(8, (ratio - 1) * 100));
            try { other.setPitch(pct); } catch (err) {
              // eslint-disable-next-line no-console
              console.error("[autodj] tick BPM-match setPitch threw:", err);
            }
          }
          try { await other.play(); } catch (err) {
            // eslint-disable-next-line no-console
            console.error(`[autodj] tick other.play(${otherD}) threw:`, err);
          }
          // eslint-disable-next-line no-console
          console.log(`[autodj] starting runCrossfade ${D}→${otherD} (${xfTime}s); pre-xfader=${eng.crossfader?.toFixed(2)}`);
          // Crossfade the active deck out, the new deck in.
          // R7 cleanup: previously a `userForcedMix` ternary lived here
          // but the ref it depended on was removed; left at runtime as a
          // ReferenceError swallowed by the surrounding try/catch which
          // is exactly what kept Auto-DJ from advancing past track 1.
          await runCrossfade(D, otherD, xfTime);
          // eslint-disable-next-line no-console
          console.log(`[autodj] runCrossfade done; post-xfader=${eng.crossfader?.toFixed(2)}; advancing currentIndex ${currentIndexRef.current}→${pick.index} activeDeck ${activeDeckRef.current}→${otherD}`);
          // Advance the cursor. From now on the tick reads the NEW active deck.
          currentIndexRef.current = pick.index; setCurrentIndex(pick.index);
          activeDeckRef.current   = otherD;     setActiveDeck(otherD);
          onLogEvent?.(`Auto-DJ: → ${pick.track.name}`);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(`[autodj] tick crossfade FAILED:`, e);
          onLogEvent?.(`Auto-DJ load failed: ${e?.message || e}`);
          try { window.dispatchEvent(new CustomEvent("sr-toast", { detail: { type: "error", msg: `Auto-DJ transition failed: ${e?.message || e}` } })); } catch { /* */ }
        }

        blendingRef.current = false;
        setBlending(false);
        setCountdown(null);
      }

      id = setTimeout(tick, 500);
    };

    tick();
    return () => { stopped = true; clearTimeout(id); };
  }, [enabled]);

  // -- Mix Now click handling --
  // Phase A R7 B1 — Mix Now must actually advance the playlist when one
  // exists, regardless of Auto-DJ ON/OFF. The R6 implementation only
  // poked a signal for the tick to handle, which never fired when
  // Auto-DJ was OFF and was racy when ON. Now Mix Now ALWAYS:
  //   (1) tries to load `playlist[currentIndex + 1]` onto the OTHER deck
  //       and crossfade — advancing the cursor.
  //   (2) Falls back to engine.mixNow() (two-loaded-decks crossfade) if
  //       there's no next playable track in the queue.
  const handleMixNow = async () => {
    const eng = engineRef.current;
    if (!eng) return;
    // If a tick-driven blend is already running, leave it alone.
    if (blendingRef.current) {
      onLogEvent?.("Mix Now: blend already in progress, ignored");
      return;
    }
    const pick = pickNext(currentIndexRef.current);
    if (pick) {
      const activeD = activeDeckRef.current;
      const otherD  = activeD === "A" ? "B" : "A";
      const xfTime  = Math.max(2, crossfadeRef.current || 8);
      // eslint-disable-next-line no-console
      console.log(`[Mix Now] from=${activeD} to=${otherD} next='${pick.track.name}' xf=${xfTime}s currentIndex=${currentIndexRef.current}→${pick.index}`);
      blendingRef.current = true;
      setBlending(true);
      setWarning("");
      try {
        const meta = await onLoadNext?.(pick.track, otherD);
        const deck  = activeD === "A" ? eng.deckA : eng.deckB;
        const other = otherD  === "A" ? eng.deckA : eng.deckB;
        if (bpmMatchRef.current && deck?.effectiveBpm && meta?.bpm) {
          const ratio = deck.effectiveBpm / meta.bpm;
          const pct = Math.max(-8, Math.min(8, (ratio - 1) * 100));
          try { other.setPitch(pct); } catch (err) {
            // eslint-disable-next-line no-console
            console.error("[Mix Now] BPM-match setPitch threw:", err);
          }
        }
        try { await other.play(); } catch (err) {
          // eslint-disable-next-line no-console
          console.error(`[Mix Now] other.play(${otherD}) threw:`, err);
        }
        await runCrossfade(activeD, otherD, xfTime);
        currentIndexRef.current = pick.index; setCurrentIndex(pick.index);
        activeDeckRef.current   = otherD;     setActiveDeck(otherD);
        onLogEvent?.(`Mix Now: → ${pick.track.name}`);
      } catch (e) {
        onLogEvent?.(`Mix Now load failed: ${e}`);
        // eslint-disable-next-line no-console
        console.warn("[Mix Now] load/crossfade threw", e);
      }
      blendingRef.current = false;
      setBlending(false);
      return;
    }
    // No next track in queue — delegate to engine.mixNow for the simple
    // two-loaded-decks crossfade case.
    // eslint-disable-next-line no-console
    console.log("[Mix Now] no next playlist track; falling back to engine.mixNow");
    const res = onMixNow?.();
    if (res === "no-op") {
      onLogEvent?.("Mix Now: load a track on the other deck first.");
    }
  };

  // Reset cursor when the playlist is cleared so the next enable autostarts cleanly.
  useEffect(() => {
    if (playlist.length === 0 && currentIndexRef.current >= 0) {
      currentIndexRef.current = -1;
      setCurrentIndex(-1);
    }
  }, [playlist.length]);

  const movePl = (from, to) => {
    setPlaylist((p) => {
      const next = [...p];
      const [it] = next.splice(from, 1);
      next.splice(to, 0, it);
      return next;
    });
  };
  const removePl = (idx) => setPlaylist((p) => p.filter((_, i) => i !== idx));

  return (
    <div data-testid="auto-dj-panel" className="text-xs space-y-2">
      {/* Phase A R9 A — UX tip. Reminding the DJ to pre-load decks A and B
          before tapping Start avoids the "click Start, nothing happens"
          confusion when the playlist is empty or only one deck is hot. */}
      <div data-testid="auto-dj-prestart-tip"
           className="flex items-start gap-2 rounded border border-amber-400/30 bg-amber-400/10 px-2.5 py-1.5 text-[11px] leading-snug text-amber-100/90">
        <Lightbulb className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-300" />
        <div>
          <span className="font-semibold">Tip:</span>{" "}
          Load Deck A and Deck B with your first tracks before starting Auto-DJ for the smoothest start.
        </div>
      </div>
      {/* Header — Auto-DJ toggle + Mix Now button (Phase A R6 B1) */}
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">Auto-DJ</div>
          <div className="font-semibold truncate">{enabled ? "ON" : "OFF"} · {playlist.length} tracks queued</div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {/* Phase A R6 B1 — Mix Now lives here, inside the AutoDjPanel.
              When Auto-DJ is ON, it advances to the next track immediately.
              When OFF, it falls back to the engine-level manual crossfade. */}
          <button onClick={handleMixNow}
                  title={enabled ? "Crossfade to the next playlist track NOW" : "Manually crossfade to the other deck"}
                  className="px-3 py-1.5 rounded font-bold text-xs uppercase tracking-[0.08em] bg-purple-500/20 hover:bg-purple-500/40 text-purple-100 border border-purple-400/40"
                  data-testid="dj-mix-now">
            ↔ Mix Now
          </button>
          <button onClick={async () => {
                    const next = !enabled;
                    // eslint-disable-next-line no-console
                    console.log(`[autodj] toggle clicked enabled: ${enabled} → ${next} playlist.length=${playlist.length}`);
                    // Resume AudioContext on enable (required for autoplay policy)
                    if (next && engineRef.current?.ctx?.state === "suspended") {
                      try { await engineRef.current.ctx.resume(); } catch { /* */ }
                    }
                    setEnabled(next);
                  }}
                  className={`px-3 py-1.5 rounded font-bold text-xs ${enabled ? "bg-emerald-500 text-black" : "bg-white/10 text-white"}`}
                  data-testid="auto-dj-toggle">
            {enabled ? <><Pause className="inline w-3 h-3 mr-1" /> Stop</> : <><Play className="inline w-3 h-3 mr-1" /> Start</>}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-1 text-[11px]">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input type="checkbox" checked={keyMatch} onChange={(e) => setKeyMatch(e.target.checked)}
                 data-testid="auto-dj-key-match-toggle" />
          <span>Key match</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input type="checkbox" checked={bpmMatch} onChange={(e) => setBpmMatch(e.target.checked)}
                 data-testid="auto-dj-bpm-match-toggle" />
          <span>BPM match</span>
        </label>
      </div>

      <div>
        <div className="flex justify-between text-[10px] uppercase tracking-[0.22em] text-white/40">
          <span>Crossfade</span><span data-testid="auto-dj-crossfade-time-value">{crossfade}s</span>
        </div>
        <input type="range" min={2} max={30} step={1} value={crossfade}
               onChange={(e) => setCrossfade(parseInt(e.target.value, 10))}
               className="w-full" data-testid="auto-dj-crossfade-time" />
      </div>

      {/* Phase A R9 G — transition style. Used by both Auto-DJ tick
          crossfades AND manual Mix Now. */}
      <div>
        <div className="flex justify-between text-[10px] uppercase tracking-[0.22em] text-white/40 mb-1">
          <span>Transition style</span>
        </div>
        <select
          value={transitionStyle}
          onChange={(e) => setTransitionStyle(e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white outline-none focus:border-purple-400"
          data-testid="auto-dj-transition-style"
        >
          <option value="linear">Linear crossfade — classic A→B</option>
          <option value="eqswap">EQ swap — drop outgoing bass</option>
          <option value="filter">Filter sweep — HPF on outgoing</option>
          <option value="echo">Echo out — filter slam at the end</option>
          <option value="loop">Loop + swap (beta)</option>
        </select>
      </div>

      <div>
        <div className="flex justify-between text-[10px] uppercase tracking-[0.22em] text-white/40">
          <span>Max length / track</span><span>{maxLengthSec > 0 ? `${maxLengthSec}s` : "—"}</span>
        </div>
        <input type="range" min={0} max={600} step={5} value={maxLengthSec}
               onChange={(e) => setMaxLengthSec(parseInt(e.target.value, 10))}
               className="w-full" data-testid="auto-dj-max-length" />
      </div>

      <div className="grid grid-cols-2 gap-2 pt-1 border-t border-white/10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">Set length</div>
          <div className="font-mono text-sm" data-testid="auto-dj-set-length">{fmtMs(totalMs)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">Time remaining</div>
          <div className="font-mono text-sm" data-testid="auto-dj-time-remaining">{fmtMs(remainingMs)}</div>
        </div>
      </div>

      {countdown != null && countdown > 0 && (
        <div className="text-[11px] text-emerald-300/90 bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-1"
             data-testid="auto-dj-countdown">
          Next mix in {countdown}s
        </div>
      )}
      {blending && (
        <div className="text-[11px] text-purple-300/90 bg-purple-500/10 border border-purple-500/30 rounded px-2 py-1"
             data-testid="auto-dj-blending">
          Blending {activeDeck} → {activeDeck === "A" ? "B" : "A"} ({crossfade}s)
        </div>
      )}
      {warning && (
        <div className="text-[11px] text-amber-300/90 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> {warning}
        </div>
      )}

      <div className="space-y-1 max-h-44 overflow-y-auto pr-1" data-testid="auto-dj-playlist">
        {playlist.length === 0 && (
          <div className="text-[11px] text-white/40 text-center py-3">
            Empty playlist. Use the Library panel&apos;s <Plus className="inline w-3 h-3" /> button to add tracks.
          </div>
        )}
        {playlist.map((t, i) => {
          const trackBpm    = t.detectedBpm ?? t.bpm ?? 0;
          const trackKey    = t.detectedKey ?? t.key ?? null;
          const trackDurSec = t.durationSec ?? t.duration ?? 0;
          const hasFile     = !!t.file || !!t.hasFile;
          const needsReattach = !hasFile && !!t.sourceHandle;
          const isCurrent   = i === currentIndex;
          const isPlayed    = i < currentIndex;
          return (
            <div key={t.trackId || t.id || `${t.name}-${i}`}
                 className={`flex items-center gap-2 px-2 py-1 rounded ${
                   isCurrent
                     ? "bg-emerald-500/15 border border-emerald-500/40"
                     : needsReattach
                       ? "bg-amber-500/5 border border-amber-500/30"
                       : isPlayed
                         ? "bg-white/[0.03] opacity-50"
                         : "bg-white/5"
                 }`}
                 data-testid={`auto-dj-playlist-item-${i}`}>
              <GripVertical className="w-3 h-3 text-white/30 cursor-grab"
                            draggable
                            onDragStart={(e) => e.dataTransfer.setData("text/plain", String(i))}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => { const from = parseInt(e.dataTransfer.getData("text/plain"), 10); if (!isNaN(from)) movePl(from, i); }} />
              <Music className={`w-3 h-3 shrink-0 ${isCurrent ? "text-emerald-300" : "text-white/40"}`} />
              <div className="flex-1 min-w-0">
                <div className="truncate text-[12px]" title={t.name}>
                  {isCurrent && <span data-testid={`auto-dj-current-${i}`} className="text-emerald-300 mr-1">▶</span>}
                  {t.name}
                </div>
                <div className="text-[10px] text-white/40 flex items-center gap-2">
                  {trackBpm > 0 && <span className="text-emerald-300/80">{trackBpm} BPM</span>}
                  {trackKey && <span className="text-purple-300/80">{trackKey}</span>}
                  <span>{fmtMs(trackDurSec * 1000)}</span>
                  {needsReattach && (
                    <span className="text-amber-300/90" data-testid={`auto-dj-needs-reattach-${i}`}>
                      needs re-attach
                    </span>
                  )}
                </div>
              </div>
              {needsReattach && (
                <button onClick={() => onReattach?.(t)}
                        className="px-1.5 py-0.5 rounded bg-amber-500/20 hover:bg-amber-500/40 text-amber-200 text-[10px] font-bold flex items-center gap-1"
                        title="Re-attach this track from the saved music folder"
                        data-testid={`auto-dj-reattach-${i}`}>
                  <RefreshCw className="w-3 h-3" />
                  Re-attach
                </button>
              )}
              <button onClick={() => removePl(i)}
                      className="p-1 rounded hover:bg-white/10 text-white/60"
                      data-testid={`auto-dj-remove-${i}`}>
                <X className="w-3 h-3" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
